import { CanvasDatabaseAri } from '@atlassian/ari/canvas/database';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';
import { type YDatabase } from '@databases/collab/y-database';
import { migrateJiraServerId } from '@databases/collab-migrations/migrate-jira-server-id';
import { MigrationManager } from '@databases/collab-migrations/migrations-manager';
import { logError } from '@databases/logs';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
import { container } from '@databases/service-container';
import { type DatabaseChangeType } from '@databases/types/redux';
import {
  unstable_IdlePriority as idlePriority,
  unstable_scheduleCallback as scheduleCallback,
} from 'scheduler';
import { snapshot, subscribe } from 'valtio';

import { getIdentityClient } from '@canvas/atlassian-client/identity';
import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { currentConnectionStatus } from '@canvas/canvas-state/connection-status';
import {
  BrowserWaiter,
  createYjsSocket,
  getDocumentV2,
  getRemoteStepsUpdate,
  persistLargeStep,
  type YjsSocket,
} from '@canvas/connection';
import { type CollabStrategy } from '@canvas/connection/collab-strategy';
import { getIdentity } from '@canvas/connection/identity';
import { type FrameToken } from '@canvas/frame-api-types';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { AwarenessChannel } from '@canvas/model/awareness';
import { type Identity } from '@canvas/model/identity';
import { Action } from '@canvas/model/permissions';
import { type ClientId } from '@canvas/model/telepointers';
import { TokenManagerV1 } from '@canvas/token-manager';

import { type DatabaseChangeset } from '../../database/changesets';
import { addChangeToYjs } from '../redux-to-yjs/add-change-to-yjs';

import { type TelepointerState, YDatabaseTelepointers } from './ydatabase-telepointers';

type DoSynchronizeOpts = {
  grants: Set<Action>;
  alreadyCreated: boolean;
  canCreate: boolean;
};

const isSameSet = (a: Set<any>, b: Set<any>) => {
  if (a.size !== b.size) {
    return false;
  }

  for (const aa of a) {
    if (!b.has(aa)) {
      return false;
    }
  }
  return true;
};

const getChangesetTimingStats = (
  keyedTimes: Partial<{ [K in DatabaseChangeType]: number[] }>,
  computeStartTime: number
) => {
  const results: ChangesetTimingStats = {};

  (Object.keys(keyedTimes) as Array<DatabaseChangeType>).forEach((timeKey) => {
    let min = 0;
    let max = 0;
    let avg = 0;
    let index = 0;
    let below50count = 0;

    const times = keyedTimes[timeKey]!;
    times.forEach((time) => {
      if (time !== 0) {
        avg = (avg * index + time) / ++index;
        if (time > max) {
          max = time;
        }
        if (min === 0 || time < min) {
          min = time;
        }
        if (time < 50) {
          below50count++;
        }
      }
    });

    results[timeKey] = {
      min,
      max,
      avg,
      count: index,
      below50count,
      compute: performance.now() - computeStartTime,
    };
  });

  return results;
};

type OnPermissionsChangedHandler = (readOnly: boolean) => void;
type OnErrorHandler = (error: unknown) => void;

type OnLoadedHandler = (db: YDatabase) => void;
type OnTelepointerChangeHandler = (
  clientId: number,
  entryId: TelepointerState | undefined
) => void;
type OnClientIdInitHandler = (clientId: ClientId) => void;
type OnParticipantsUpdateHandler = (participants: Record<number, Identity>) => void;

/**
 * VersionId is a unique identifier for a version of a document.
 * The absence of VersionId means the document is the latest version.
 * **/
type VersionId = string & { __versionId: symbol };

/**
 * Extension of the VersionId type used by the frontend only, where the latest
 * version is represented as the string 'latest'.
 */
type FrontendVersionId = 'latest' | VersionId;

/**
 * Converts a string to a FrontendVersionId.
 */
const toFrontendVersionId = (e: string): FrontendVersionId => e as FrontendVersionId;

type ChangesetTimingStats = Partial<{
  [K in DatabaseChangeType]: {
    min: number;
    max: number;
    avg: number;
    count: number;
    below50count: number;
    compute: number;
  };
}>;

let onStartChangesetStats: () => void | undefined;
let onEndChangesetStats: (stats: ChangesetTimingStats) => void | undefined;

export function setOnStartChangesetStats(handler: () => void) {
  onStartChangesetStats = handler;
}

export function setOnEndChangesetStats(handler: (stats: ChangesetTimingStats) => void) {
  onEndChangesetStats = handler;
}

export class CollabManager {
  tokenManager = new TokenManagerV1();
  databaseAri: CanvasDatabaseAri;
  wasCreatedThisSession: boolean;
  tsubmit: NodeJS.Timeout | undefined;
  #database: YDatabase;
  #socket: YjsSocket | undefined;
  private grants: Set<Action> = new Set();
  /**
   * Mutex for enforcing one synchronization at a time
   */
  private synchronizing: Promise<void> | undefined;
  private onErrorHandlers: Array<OnErrorHandler> = [];
  private onLoadHandlers: Array<OnLoadedHandler> = [];
  private onPermissionsChangedHandlers: Array<OnPermissionsChangedHandler> = [];
  private onTelepointerChangeHandlers: Array<OnTelepointerChangeHandler> = [];
  private onClientIdInitHandlers: Array<OnClientIdInitHandler> = [];
  private onParticipantsUpdateHandlers: Array<OnParticipantsUpdateHandler> = [];
  private times: Partial<{ [K in DatabaseChangeType]: number[] }> = {};
  private telepointers: YDatabaseTelepointers | null = null;

  constructor(
    private cloudId: string,
    private spaceId: string,
    private contentId: string,
    private referenceId: string,
    ydb: YDatabase,
    private token?: FrameToken,
    private versionId?: string | undefined
  ) {
    this.databaseAri = CanvasDatabaseAri.create({
      siteId: cloudId,
      databaseId: referenceId,
    });

    this.tokenManager.onRefreshToken(async () => {
      return await this.getToken();
    });

    this.tokenManager.onSynchronizeClaims(this.onPermissionsUpdate.bind(this));

    this.#database = ydb;

    this.wasCreatedThisSession = false;
  }

  onLoaded(handler: OnLoadedHandler) {
    this.onLoadHandlers.push(handler);
  }

  onPermissionsChanged(handler: OnPermissionsChangedHandler): () => void {
    this.onPermissionsChangedHandlers.push(handler);

    return () => {
      const index = this.onPermissionsChangedHandlers.indexOf(handler);

      if (index !== -1) {
        this.onPermissionsChangedHandlers.splice(index, 1);
      }
    };
  }

  onError(handler: OnErrorHandler) {
    this.onErrorHandlers.push(handler);
  }

  onTelepointerChange(handler: OnTelepointerChangeHandler) {
    this.onTelepointerChangeHandlers.push(handler);
  }

  onClientIdInit(handler: OnClientIdInitHandler) {
    this.onClientIdInitHandlers.push(handler);
  }

  onParticipantsUpdate(handler: OnParticipantsUpdateHandler) {
    this.onParticipantsUpdateHandlers.push(handler);
  }

  updateTelepointer(entryId?: string, fieldId?: string) {
    this.telepointers?.setCursorPosition(entryId, fieldId);
  }

  get database() {
    return this.#database;
  }

  get clientId(): ClientId | null {
    return this.telepointers?.awareness.clientID || null;
  }

  get telepointerStates(): Record<ClientId, TelepointerState> {
    return this.telepointers?.telepointers || {};
  }

  get participants(): Record<ClientId, Identity> {
    return this.telepointers?.participants || {};
  }

  onPermissionsUpdate(
    id: string | undefined,
    opts: {
      board: Set<Action>;
    }
  ) {
    this.onPermissionsChangedHandlers.forEach((handler) =>
      handler(!opts.board.has(Action.write))
    );
    this.synchronize(opts.board);
  }

  setGrantsWithoutSync(grants: Set<Action>) {
    this.grants = grants;
  }

  private async getToken(): Promise<FrameToken | undefined> {
    // In local dev online mode, reuse the initial token to skip Confluence API calls
    // TODO: Replace USE_LOCAL_CANVAS_BACKEND with proper 'online' mode once databases
    // mocks out all network calls like whiteboards does.
    if (process.env.USE_LOCAL_CANVAS_BACKEND === 'true') {
      return this.token;
    }

    const service = getConfluenceService();

    try {
      const bulkTokens = await service.getCanvasSessionBulkTokens({
        contentIds: [this.contentId],
      });

      const token = bulkTokens[0].data;

      const containerAri = ConfluenceSpaceAri.create({
        spaceId: this.spaceId,
        siteId: this.cloudId,
      });

      return {
        container: containerAri.toString(),
        board: this.databaseAri.toString(),
        accessToken: token.access.token,
        mediaToken: token.media.token,
        mediaBaseUrl: token.media.baseUrl,
        mediaCollectionId: token.media.contentId,
      };
    } catch (error) {
      logError('Failed to get canvas session token', error as Error);

      return;
    }
  }

  async createSession(grants: Set<Action>) {
    this.#socket = await this.connectSocket(!grants.has(Action.write));
    this.grants = grants;
  }

  killSession() {
    if (this.#socket) {
      this.#socket.destroy();
      this.#socket = undefined;
    }

    if (this.telepointers) {
      this.telepointers.dispose();
      this.telepointers = null;
    }
  }

  async synchronize(grants: Set<Action>) {
    const alreadyCreated = !!this.#socket;
    const canCreate = grants.size > 0;

    // No work if empty grants
    if (grants.size === 0) {
      return;
    }

    try {
      if (this.synchronizing) {
        await this.synchronizing;
        this.synchronizing = undefined;
      }

      const synchronizing = this.doSynchronize({
        grants,
        alreadyCreated,
        canCreate,
      });
      this.synchronizing = synchronizing;

      await this.synchronizing;
      this.synchronizing = undefined;
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (err) {
      this.onErrorHandlers.forEach((handler) => handler(err));
    }
  }

  private async doSynchronize({ grants, alreadyCreated, canCreate }: DoSynchronizeOpts) {
    if (alreadyCreated) {
      await this.synchronizeGrants(grants);
    } else if (canCreate) {
      await this.createSession(grants);
    }
  }

  private async synchronizeGrants(grants: Set<Action>) {
    // only sync if current grants & pending grants differ
    const grantsSame = isSameSet(this.grants, grants);
    if (grantsSame) {
      return;
    }

    await this.killSession();
    await this.createSession(grants);
  }

  async connect() {
    if (!this.token) {
      this.token = await this.getToken();
    }
    this.tokenManager.updateToken(this.token);
  }

  get isReadOnly() {
    return !this.grants.has(Action.write);
  }

  private async connectSocket(isReadOnly: boolean) {
    const metric = ReliabilityMetrics.getInstance().start(MetricName.COLLAB_CONNECT);
    const identityClient = getIdentityClient();
    const serverUrl = `/gateway/api/canvas`;

    const refresher = async () => {
      await this.tokenManager.refreshToken();
    };

    const documentId = this.databaseAri.toString();

    /**
     * A user can go through the sync flow only if there is not a version id
     * present or if they are able to write to a
     * database. Reasoning: The sync protocol involves synchronisation of changes
     * across users, which can lead to changes in a database contents.
     */
    const canSync = this.versionId ? false : !isReadOnly;

    /**
     * A user can propagate updates only if they are allowed to write to a database.
     */
    const canUpdate = !isReadOnly;

    const version = this.versionId ? toFrontendVersionId(this.versionId) : undefined;

    const provider = createYjsSocket({
      accessTokenSupplier: async () => {
        return (await this.tokenManager.token()).accessToken;
      },
      relaySupplier: (
        attempt: number,
        _signal: AbortSignal,
        collabStrategy: CollabStrategy
      ) => {
        return collabStrategy.getRelayUrl({
          boardId: this.referenceId,
          attempt,
          resourceType: 'database',
        });
      },
      identitySupplier: getIdentity,
      userSupplier: identityClient.loadFullIdentityProfiles.bind(identityClient),
      existingDocument: this.#database.doc,
      getDocument: async (signal) =>
        await getDocumentV2({
          documentId,
          serverUrl,
          signal,
          tokenManager: this.tokenManager,
          versionId: version,
        }),
      boardId: this.referenceId,
      persistLargeStep: (step) =>
        persistLargeStep({
          documentId,
          serverUrl,
          step,
          tokenManager: this.tokenManager,
        }),
      getRemoteStepsUpdate: (headStep, attempt, signal, collabStrategy) =>
        getRemoteStepsUpdate({
          headStep,
          documentId: this.referenceId,
          attempt,
          signal,
          tokenManager: this.tokenManager,
          collabStrategy,
          refreshTokenFn: refresher,
          serverUrl,
        }),
      canSync,
      canUpdate,
      debounceWaiter: new BrowserWaiter(),
      versionId: version,
      useVersionHistory: true,
    });

    const meta = provider.doc.getMap('_meta');
    const clientDocVersion = MigrationManager.getClientDocVersion();

    provider.addListener('indexedDbSynced', () => {
      const remoteVersion = (meta.get('version') as number | undefined) || 0;

      if (remoteVersion < clientDocVersion) {
        // migrations haven't run or not up to date
        // wait for initialLoad event to trigger migration or update
        return;
      }

      this.onLoadHandlers.map((handler) => handler(this.#database));
    });

    provider.addListener('initialLoad', async () => {
      const remoteVersion = (meta.get('version') as number | undefined) || 0;
      if (remoteVersion === 0) {
        this.wasCreatedThisSession = true;
      }

      const migrationManager = new MigrationManager(
        documentId,
        this.#database,
        container.resolve('Intl'),
        remoteVersion
      );

      await migrationManager.runPending();

      if (remoteVersion < clientDocVersion) {
        meta.set('version', clientDocVersion);
      }

      migrateJiraServerId(documentId, this.#database);

      this.#database.undoManager.clear();
      this.onLoadHandlers.map((handler) => handler(this.#database));
    });

    provider.addListener('connected', () => {
      metric.success();
      currentConnectionStatus.connectionStatus = 'online';
    });

    provider.addListener('fatalError', (error: Error) => {
      metric?.failure();
      logError('Collab connection fatalError', error);
    });

    provider.addListener('failingRetry', (error: Error) => {
      metric?.failure();
      logError('Collab connection failingRetry', error);
    });

    provider.addListener('connecting', (attempt) => {
      if (attempt > 1) {
        currentConnectionStatus.connectionStatus = 'offline';
      }
    });

    provider.addListener('disconnected', () => {
      currentConnectionStatus.connectionStatus = 'offline';
    });

    const telepointerAwareness = provider.awarenessContainer.getAwareness(
      AwarenessChannel.Telepointer
    );

    this.telepointers = new YDatabaseTelepointers(
      telepointerAwareness,
      getIdentity,
      identityClient.loadFullIdentityProfiles.bind(identityClient)
    );

    this.onClientIdInitHandlers.forEach((handler) => {
      handler(telepointerAwareness.clientID);
    });

    subscribe(this.telepointers.telepointers, (ops) => {
      ops
        .flatMap(([_, clientIds]) => clientIds as unknown as ClientId[])
        .forEach((clientId: ClientId) => {
          this.onTelepointerChangeHandlers.forEach((handler) => {
            handler(clientId, this.telepointers?.telepointers[clientId]);
          });
        });
    });

    subscribe(this.telepointers.participants, () => {
      this.onParticipantsUpdateHandlers.forEach((handler) => {
        if (this.telepointers?.participants) {
          handler(snapshot(this.telepointers.participants));
        }
      });
    });

    return provider;
  }

  end = () => {
    const timesCopy = { ...this.times };
    this.times = {};
    scheduleCallback(idlePriority, () => {
      const computeStartTime = performance.now();
      const data = getChangesetTimingStats(timesCopy, computeStartTime);
      onEndChangesetStats?.(data);
    });
  };

  applyChangeset(changeset: DatabaseChangeset, state: DefaultRootState | undefined) {
    onStartChangesetStats?.();

    for (const change of changeset) {
      const startTime = performance.now();

      addChangeToYjs(this.#database, change, state);

      const endTime = performance.now();
      const time = endTime - startTime;

      if (!this.times[change.type]) {
        this.times[change.type] = [];
      }

      this.times[change.type]!.push(time);
    }

    setTimeout(() => {
      clearTimeout(this.tsubmit);
      this.tsubmit = setTimeout(this.end, 2000); // debounce
    }, 0);
  }

  /**
   * Calls the given callback, grouping any YJS mutations that happen during its execution into a single
   * YJS transaction. This can be useful to make the undo manager register the change as a single action.
   * Does not support async callbacks.
   */
  batch(callback: () => void): void {
    this.#database.mutate(callback);
  }
}
